import React from 'react';

import {
  createIdempotencyKey,
  getPaymentOperations,
  settlePaymentOperation,
} from '../../api/rental';
import Layout from './Layout';
import {
  ActionButton,
  Actions,
  Badge,
  Card,
  CardTitle,
  DefinitionList,
  ErrorText,
  Field,
  FieldGrid,
  Input,
  Scroll,
  State,
  SuccessText,
  Table,
  Textarea,
} from './styles';

const TYPE_TITLES = {
  prepayment: 'Оплата аренды',
  deposit: 'Депозит',
  refund: 'Возврат по аренде',
  deposit_refund: 'Возврат депозита',
  overrun: 'Доплата',
  charge: 'Начисление',
};

const STATUS_TITLES = {
  pending: 'Ожидает подтверждения',
  processing: 'В обработке',
  retry_pending: 'Повтор после ошибки',
  failed: 'Ошибка',
  succeeded: 'Завершена',
};

const formatAmount = value => `${(Number(value) / 100).toLocaleString('ru-RU')} ₽`;
const formatDateTime = value => (value
  ? new Date(value).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
  })
  : '—');

const toLocalInputValue = date => {
  const pad = value => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const blankForm = operation => ({
  amount: String(Number(operation.amount) / 100),
  reference: '',
  reason: '',
  comment: '',
  settled_at: toLocalInputValue(new Date()),
});

function PaymentOperationsContent({authorization}) {
  const [operations, setOperations] = React.useState([]);
  const [showAll, setShowAll] = React.useState(false);
  const [state, setState] = React.useState('loading');
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [selected, setSelected] = React.useState(null);
  const [form, setForm] = React.useState(null);

  const load = React.useCallback(async () => {
    if (!authorization) { return; }
    setState('loading');
    try {
      const list = await getPaymentOperations(
        authorization,
        showAll ? {all: 'true'} : {},
      );
      setOperations(list);
      setState('ready');
    } catch (requestError) {
      setError(requestError.message);
      setState('ready');
    }
  }, [authorization, showAll]);

  React.useEffect(() => { load(); }, [load]);

  const select = operation => {
    setSelected(operation);
    setForm(operation ? blankForm(operation) : null);
    setError('');
    setSuccess('');
  };

  const submit = async () => {
    setError('');
    setSuccess('');
    const amount = Math.round(Number(form.amount) * 100);
    if (!Number.isInteger(amount) || amount <= 0) {
      setError('Сумма подтверждения должна быть положительной');
      return;
    }
    if (!form.reference.trim() || !form.reason.trim()) {
      setError('Внешний референс и основание обязательны: без них закрытие нечем подтвердить');
      return;
    }
    const settledAt = new Date(form.settled_at);
    if (Number.isNaN(settledAt.getTime())) {
      setError('Некорректная дата урегулирования');
      return;
    }

    setState('saving');
    try {
      const response = await settlePaymentOperation(
        authorization,
        selected.id,
        {
          amount,
          reference: form.reference.trim(),
          reason: form.reason.trim(),
          comment: form.comment.trim() || null,
          settled_at: settledAt.toISOString(),
        },
        createIdempotencyKey(`settle-${selected.id}`),
      );
      setSuccess(`Операция закрыта вручную, референс ${response.operation.manual_reference}`);
      select(null);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setState('ready');
    }
  };

  return (
    <div>
      <Card>
        <CardTitle>Платёжные операции</CardTitle>
        <Note>
          Ручное закрытие не выполняет платёж. Сначала верните деньги вне системы и дождитесь
          подтверждения провайдера или банка — закрытие только фиксирует внешний референс и
          снимает операцию с автоматических повторов.
        </Note>

        <Actions>
          <ActionButton onClick={() => setShowAll(current => !current)}>
            {showAll ? 'Только незакрытые' : 'Показать все'}
          </ActionButton>
          <ActionButton onClick={load}>Обновить</ActionButton>
        </Actions>

        {error && !selected && <ErrorText>{error}</ErrorText>}
        {success && <SuccessText>{success}</SuccessText>}

        {state === 'loading' && <State>Загрузка…</State>}
        {state !== 'loading' && operations.length === 0 && (
          <State>
            {showAll
              ? 'Операций пока нет.'
              : 'Зависших операций нет — все возвраты и доплаты прошли через провайдера.'}
          </State>
        )}

        {operations.length > 0 && (
          <Scroll>
            <Table minWidth="900px">
              <thead>
                <tr>
                  <th>Операция</th>
                  <th>Сумма</th>
                  <th>Состояние</th>
                  <th>Автомобиль</th>
                  <th>Клиент</th>
                  <th>Попытки</th>
                  <th>Создана</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {operations.map(operation => (
                  <tr key={operation.id}>
                    <td>{TYPE_TITLES[operation.operation_type] || operation.operation_type}</td>
                    <td>{formatAmount(operation.amount)}</td>
                    <td>
                      <Badge
                        danger={['retry_pending', 'failed'].includes(operation.status)}
                        success={operation.status === 'succeeded'}
                      >
                        {STATUS_TITLES[operation.status] || operation.status}
                      </Badge>
                      {operation.settled_manually && <div>Закрыта вручную</div>}
                    </td>
                    <td>
                      {operation.vehicle
                        ? `${operation.vehicle.brand} ${operation.vehicle.model}`
                        : '—'}
                      <div>{operation.vehicle && operation.vehicle.license_plate}</div>
                    </td>
                    <td>{operation.user_phone || '—'}</td>
                    <td>{operation.retry_count}</td>
                    <td>{formatDateTime(operation.created_at)}</td>
                    <td>
                      {!operation.settled_manually && operation.status !== 'succeeded' && (
                        <ActionButton onClick={() => select(operation)}>Закрыть вручную</ActionButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Scroll>
        )}
      </Card>

      {selected && (
        <Card style={{marginTop: 24}}>
          <CardTitle>
            Ручное закрытие · {TYPE_TITLES[selected.operation_type] || selected.operation_type}
          </CardTitle>

          <DefinitionList>
            <dt>Сумма операции</dt>
            <dd>{formatAmount(selected.amount)}</dd>
            <dt>Аренда</dt>
            <dd>{selected.rental_id}</dd>
            <dt>Последняя ошибка</dt>
            <dd>{selected.last_error || '—'}</dd>
            <dt>Попыток</dt>
            <dd>{selected.retry_count}</dd>
          </DefinitionList>

          <FieldGrid columns={2} style={{marginTop: 20}}>
            <Field>
              <span>Сумма подтверждения, ₽</span>
              <Input
                value={form.amount}
                onChange={event => setForm({...form, amount: event.target.value})}
              />
            </Field>
            <Field>
              <span>Дата и время урегулирования</span>
              <Input
                type="datetime-local"
                value={form.settled_at}
                onChange={event => setForm({...form, settled_at: event.target.value})}
              />
            </Field>
            <Field>
              <span>Внешний референс</span>
              <Input
                value={form.reference}
                placeholder="Номер операции в кабинете эквайринга"
                onChange={event => setForm({...form, reference: event.target.value})}
              />
            </Field>
            <Field>
              <span>Основание</span>
              <Input
                value={form.reason}
                placeholder="Номер обращения или решение ответственного"
                onChange={event => setForm({...form, reason: event.target.value})}
              />
            </Field>
          </FieldGrid>

          <Field style={{marginTop: 16}}>
            <span>Комментарий</span>
            <Textarea
              short
              rows={2}
              value={form.comment}
              onChange={event => setForm({...form, comment: event.target.value})}
            />
          </Field>

          <Warning>
            Закрывать операцию до фактического подтверждения запрещено: после закрытия система
            перестанет повторять возврат.
          </Warning>

          {error && <ErrorText>{error}</ErrorText>}

          <Actions>
            <ActionButton primary disabled={state === 'saving'} onClick={submit}>
              {state === 'saving' ? 'Закрытие…' : 'Закрыть операцию'}
            </ActionButton>
            <ActionButton onClick={() => select(null)}>Отменить</ActionButton>
          </Actions>
        </Card>
      )}
    </div>
  );
}

function PaymentOperations() {
  return (
    <Layout
      title="Платёжные операции"
      description="Очередь возвратов, доплат и начислений. Здесь же вручную закрывается операция, которую провайдер не принимает по API."
    >
      <PaymentOperationsContent />
    </Layout>
  );
}

const Note = ({children}) => (
  <div style={{
    marginBottom: 16,
    padding: '12px 14px',
    borderRadius: 8,
    background: '#F5F7F9',
    color: '#2F3033',
    fontSize: 13,
    lineHeight: 1.5,
  }}>{children}</div>
);

const Warning = ({children}) => (
  <div style={{
    margin: '18px 0 0',
    padding: '12px 14px',
    borderRadius: 8,
    background: '#fff5f3',
    color: '#DF2D14',
    fontSize: 13,
    lineHeight: 1.5,
  }}>{children}</div>
);

export default PaymentOperations;
